import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  LinearProgress,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import {
  CloudUpload,
  CloudDownload,
  Refresh,
} from "@material-ui/icons";
import axios from "axios";
import moment from "moment";
import Loader from "./Loader";

const PortForwardingBackup = ({ deviceUuid, setError, setMessage }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [backups, setBackups] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [createBackupDialog, setCreateBackupDialog] = useState(false);
  const [transferDialog, setTransferDialog] = useState(false);
  const [transferForm, setTransferForm] = useState({
    fromUnitSerial: "",
    toUnitSerial: "",
  });
  const [availableDevices, setAvailableDevices] = useState([]);

  const fetchPfRules = useCallback(() => {
    setLoading(true);
    axios
      .get(`/device/${deviceUuid}/config_backup/pf_rules`)
      .then((response) => {
        setBackups(response.data.backups || []);
      })
      .catch((error) => {
        setError(error?.response?.data?.detail || "Failed to fetch port forwarding rules");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [deviceUuid, setError]);

  const createBackup = () => {
    setLoading(true);
    axios
      .post(`/device/${deviceUuid}/config_backup/pf_rules/create`)
      .then((response) => {
        setMessage("Port forwarding rules backup created successfully");
        setCreateBackupDialog(false);
        fetchPfRules();
      })
      .catch((error) => {
        setError(error?.response?.data?.detail || "Failed to create backup");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchAvailableDevices = useCallback(() => {
    // This would typically fetch a list of available devices for transfer
    // For now, we'll use a placeholder
    // setAvailableDevices([
    //   { serial: "DEV001", name: "Device 1" },
    //   { serial: "DEV002", name: "Device 2" },
    // ]);
  }, []);

  const transferPfRules = () => {
    if (!transferForm.fromUnitSerial || !transferForm.toUnitSerial) {
      setError("Please select both source and destination devices");
      return;
    }

    setLoading(true);
    axios
      .put(`/device/${deviceUuid}/config_backup/set_pf_rules`, {
        unit_serial: transferForm.toUnitSerial,
        from_unit_serial: transferForm.fromUnitSerial,
      })
      .then((response) => {
        setMessage("Port forwarding rules transferred successfully");
        setTransferDialog(false);
        setTransferForm({ fromUnitSerial: "", toUnitSerial: "" });
        fetchPfRules();
      })
      .catch((error) => {
        setError(error?.response?.data?.detail || "Failed to transfer port forwarding rules");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const uploadPfRules = () => {
    if (!selectedFile) {
      setError("Please select a file to upload");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    setUploading(true);
    setUploadProgress(0);

    axios
      .post(`/device/${deviceUuid}/config_backup/upload_pf_rules`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      })
      .then((response) => {
        setMessage("Port forwarding rules backup uploaded successfully");
        setSelectedFile(null);
        setUploadProgress(0);
        fetchPfRules();
        // Reset file input
        const fileInput = document.getElementById("pf-file-input");
        if (fileInput) fileInput.value = "";
      })
      .catch((error) => {
        setError(error?.response?.data?.detail || "Failed to upload port forwarding rules");
      })
      .finally(() => {
        setUploading(false);
      });
  };


  const downloadBackup = (download_link) => {
    try {
      // Create a temporary anchor element and trigger download
      const link = document.createElement("a");
      link.href = download_link;
      link.target = "_blank"; // Always open in new tab
      
      // Try to extract filename from the link, fallback to default
      const urlParts = download_link.split("/");
      const filename = urlParts[urlParts.length - 1] || "pf_backup.bak";
      
      // Set download attribute to force download if it's a file
      link.download = filename;
      
      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Also open in new tab as backup
      window.open(download_link, "_blank");
    } catch (error) {
      console.error("Download failed:", error);
      setError("Failed to download file");
    }
  };

  useEffect(() => {
    fetchPfRules();
    fetchAvailableDevices();
  }, [fetchPfRules, fetchAvailableDevices]);


  if (loading) {
    return <Loader />;
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Port Forwarding Rules Backup</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUpload />}
            onClick={() => setCreateBackupDialog(true)}
            disabled={loading}
          >
            Create Backup
          </Button>
          {/* <Button
            variant="outlined"
            color="primary"
            startIcon={<Settings />}
            onClick={() => setTransferDialog(true)}
            disabled={loading}
          >
            Transfer Rules
          </Button> */}
          <IconButton onClick={fetchPfRules} disabled={loading}>
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {/* Upload Section */}
      <Card style={{ marginBottom: 16 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Upload Port Forwarding Rules
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <input
                accept=".json,.txt"
                style={{ display: "none" }}
                id="pf-file-input"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="pf-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  disabled={uploading}
                >
                  Select Rules File
                </Button>
              </label>
            </Grid>
            {selectedFile && (
              <Grid item>
                <Typography variant="body2">
                  Selected: {selectedFile.name}
                </Typography>
              </Grid>
            )}
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={uploadPfRules}
                disabled={!selectedFile || uploading}
                startIcon={<CloudUpload />}
              >
                {uploading ? "Uploading..." : "Upload Rules"}
              </Button>
            </Grid>
          </Grid>
          {uploading && (
            <Box mt={2}>
              <Typography variant="body2" gutterBottom>
                Uploading... {uploadProgress}%
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Backups Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Filename</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {backups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No port forwarding rules backups found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              backups.map((backup) => (
                <TableRow key={backup.id}>
                  <TableCell>
                    <Typography variant="body2">
                      {backup.download_link
                        ? backup.download_link.split("/").pop()
                        : `backup_${backup.id.slice(0, 8)}.bak`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {moment(backup.timestamp).local().format("lll")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      {backup.download_link && (
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={() => downloadBackup(backup.download_link)}
                          >
                            <CloudDownload />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Backup Dialog */}
      <Dialog
        open={createBackupDialog}
        onClose={() => setCreateBackupDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Port Forwarding Rules Backup</DialogTitle>
        <DialogContent>
          <Typography>
            This will create a backup of the current port forwarding rules. Are
            you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateBackupDialog(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={createBackup}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            Create Backup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Rules Dialog */}
      <Dialog
        open={transferDialog}
        onClose={() => setTransferDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Transfer Port Forwarding Rules</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Source Device"
                value={transferForm.fromUnitSerial}
                onChange={(e) =>
                  setTransferForm({
                    ...transferForm,
                    fromUnitSerial: e.target.value,
                  })
                }
                variant="outlined"
              >
                {availableDevices.map((device) => (
                  <MenuItem key={device.serial} value={device.serial}>
                    {device.name} ({device.serial})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Destination Device"
                value={transferForm.toUnitSerial}
                onChange={(e) =>
                  setTransferForm({
                    ...transferForm,
                    toUnitSerial: e.target.value,
                  })
                }
                variant="outlined"
              >
                {availableDevices.map((device) => (
                  <MenuItem key={device.serial} value={device.serial}>
                    {device.name} ({device.serial})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTransferDialog(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={transferPfRules}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            Transfer Rules
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortForwardingBackup;
